import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';

type BookingParamsState = {
  seatings: number;
  // adults: number;
  kids: number;
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  startQuartile: number;
  endQuartile: number | null;
  bookingId: string;
  changeBooking: string;
  zone: string;
  modal: string | null;
  lang: string;
  isApp: boolean;
  appVersion: string;
  name: string;
  phone: string;
  email: string;
  inTheCircus: boolean;
  ref: string;
  refSetAt: number | null;
  requestedTime: string | null;
  suggestedTime: string | null;
  selectedRelatedVenue: string | null;
  relatedVenueVariant: string | null;
  occupiedSlotSelected: boolean;
  prevWaitlistReservations: any[];
  waitlistQuartiles: number[];
};

const initialState: BookingParamsState = {
  seatings: 2,
  // adults: 2,
  kids: 0,
  year: new Date().getFullYear().toString(),
  month: (new Date().getMonth() + 1).toString(),
  day: new Date().getDate().toString(),
  hour: '19',
  minute: '00',
  startQuartile: 0,
  endQuartile: 0,
  bookingId: '',
  changeBooking: '',
  zone: 'indoors',
  modal: null,
  lang: 'en',
  isApp: false,
  appVersion: '',
  name: '',
  phone: '',
  email: '',
  inTheCircus: false,
  ref: null,
  refSetAt: null,
  requestedTime: null,
  suggestedTime: null,
  selectedRelatedVenue: null,
  relatedVenueVariant: 'venueList',
  occupiedSlotSelected: false,
  prevWaitlistReservations: [],
  waitlistQuartiles: [],
};

const paramsSlice = createSlice({
  name: 'params',
  initialState,
  reducers: {
    setSeatings: (state, action: PayloadAction<number>) => {
      state.seatings = action.payload;
    },
    setKids: (state, action: PayloadAction<number>) => {
      state.kids = action.payload;
    },
    setYear: (state, action: PayloadAction<string>) => {
      state.year = action.payload;
    },
    setMonth: (state, action: PayloadAction<string>) => {
      state.month = action.payload;
    },
    setDay: (state, action: PayloadAction<string>) => {
      state.day = action.payload;
    },
    setHour: (state, action: PayloadAction<string>) => {
      state.hour = action.payload;
    },
    setMinute: (state, action: PayloadAction<string>) => {
      state.minute = action.payload;
    },
    setStartQuartile: (state, action: PayloadAction<number>) => {
      state.startQuartile = action.payload;
    },
    setEndQuartile: (state, action: PayloadAction<number | null>) => {
      state.endQuartile = action.payload;
    },
    setBookingId: (state, action: PayloadAction<string>) => {
      state.bookingId = action.payload;
    },
    setLang: (state, action: PayloadAction<string>) => {
      state.lang = action.payload;
    },
    setChangeBooking: (state, action: PayloadAction<string>) => {
      state.changeBooking = action.payload;
    },
    setZone: (state, action: PayloadAction<string>) => {
      state.zone = action.payload;
    },
    setModal: (state, action: PayloadAction<string | null>) => {
      state.modal = action.payload;
    },
    setIsApp: (state, action: PayloadAction<boolean>) => {
      state.isApp = action.payload;
    },
    setAppVersion: (state, action: PayloadAction<string>) => {
      state.appVersion = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setInTheCircus: (state, action: PayloadAction<boolean>) => {
      state.inTheCircus = action.payload;
    },
    setRef: (state, action: PayloadAction<string>) => {
      state.ref = action.payload;
      state.refSetAt = Date.now();
    },
    setRequestedTime: (state, action: PayloadAction<string | null>) => {
      state.requestedTime = action.payload;
    },
    setSuggestedTime: (state, action: PayloadAction<string | null>) => {
      state.suggestedTime = action.payload;
    },
    setSelectedRelatedVenue: (state, action: PayloadAction<string | null>) => {
      state.selectedRelatedVenue = action.payload;
    },
    setRelatedVenueVariant: (state, action: PayloadAction<string | null>) => {
      state.relatedVenueVariant = action.payload;
    },
    setOccupiedSlotSelected: (state, action: PayloadAction<boolean>) => {
      state.occupiedSlotSelected = action.payload;
    },
    setPrevWaitlistReservations: (state, action: PayloadAction<any[]>) => {
      state.prevWaitlistReservations = action.payload;
    },
    setWaitlistQuartiles: (state, action: PayloadAction<any[]>) => {
      state.waitlistQuartiles = action.payload;
    },
    resetParams: (state) => {
      return {
        ...initialState,
        isApp: state.isApp,
        appVersion: state.appVersion,
        name: state.name,
        phone: state.phone,
        email: state.email,
        inTheCircus: state.inTheCircus,
      };
    },
  },
  extraReducers: (builder) => {
    // Booking params are client-only; never overwrite with server initialState on refresh.
    builder.addCase(HYDRATE, (state) => state);
  },
});

export const {
  setSeatings,
  setKids,
  setYear,
  setMonth,
  setDay,
  setHour,
  setMinute,
  setStartQuartile,
  setEndQuartile,
  setBookingId,
  setLang,
  setChangeBooking,
  setZone,
  setModal,
  resetParams,
  setIsApp,
  setAppVersion,
  setName,
  setPhone,
  setEmail,
  setInTheCircus,
  setRef,
  setRequestedTime,
  setSuggestedTime,
  setSelectedRelatedVenue,
  setRelatedVenueVariant,
  setOccupiedSlotSelected,
  setPrevWaitlistReservations,
  setWaitlistQuartiles
} = paramsSlice.actions;

export default paramsSlice.reducer;

// `params` is persisted in localStorage with no expiry, so without a
// lifetime a ref from an old link would be credited to every later
// booking on this device. A ref set before refSetAt existed counts as stale.
export const REF_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;

export function freshRef(
  params: { ref?: string | null; refSetAt?: number | null },
  now: number = Date.now(),
): string | null {
  if (!params?.ref || !params?.refSetAt) return null;
  return now - params.refSetAt <= REF_LIFETIME_MS ? params.ref : null;
}
